import React from 'react';

const DEFAULT_WINDOW_MS = 1500;

// Stimulus data, not theme tokens: the card's colour is a feature the
// participant compares, so it must be literal and identical in both themes.
// Same exemption as stroop.jsx.
// Must stay identical to stroop.jsx / towerOfLondon.jsx: same ink table, and
// every value clears 3:1 on both --bg-primary values (#1A1917, #F7F6F3).
const INK = {
  red: '#E5484D',
  blue: '#3E63DD',
  // Dual-theme legible green: pure #30A46C is only 2.82:1 on the light bg.
  green: '#238F5C',
  // Dual-theme legible yellow: pure #F5D90A is 1.27:1 on the light bg.
  yellow: '#A07C00',
};

const PLURAL = {
  circle: 'circles',
  square: 'squares',
  triangle: 'triangles',
  diamond: 'diamonds',
};

// Single-shape SVG. Every shape is drawn inside a 0..100 box so one viewBox
// serves all four.
function Shape({ shape, color, size }) {
  const fill = INK[color] || 'var(--text-primary)';
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      {shape === 'circle' && <circle cx="50" cy="50" r="44" fill={fill} />}
      {shape === 'square' && <rect x="8" y="8" width="84" height="84" rx="6" fill={fill} />}
      {shape === 'triangle' && <polygon points="50,6 94,92 6,92" fill={fill} />}
      {shape === 'diamond' && <polygon points="50,4 96,50 50,96 4,50" fill={fill} />}
    </svg>
  );
}

// `count` is a compared feature, so the shapes are drawn `count` times rather
// than annotated with a numeral — a digit would let the participant compare
// text instead of the figure.
function StimulusView({ stimulus, compact }) {
  const { shape, color, count } = stimulus;
  const size = compact ? 22 : 72;
  const plural = count === 1 ? shape : PLURAL[shape] || `${shape}s`;
  return (
    <span
      role="img"
      aria-label={`${count} ${color} ${plural}`}
      // Deliberately NO `title`. It used to carry "2 triangles", which is the
      // numeral this view exists not to draw: `count` is one of the three
      // compared features, so a tooltip naming it lets a pointer user read the
      // count instead of enumerating the figures — the same shortcut the
      // shapes-drawn-count-times rendering is designed to deny. The aria-label
      // above is not the same concession: a screen-reader user cannot enumerate
      // figures at all, so naming the count is the only way to give them the
      // stimulus, whereas a sighted user is being handed a shortcut.
      style={{ display: 'inline-flex', gap: compact ? 4 : 14, alignItems: 'center' }}
    >
      {Array.from({ length: count }, (_, i) => (
        <Shape key={i} shape={shape} color={color} size={size} />
      ))}
    </span>
  );
}

const sameCard = (a, b) =>
  a.shape === b.shape && a.color === b.color && a.count === b.count;

export const speedMatchTask = {
  id: 'speed-match',
  name: 'Card Sprint',
  pillar: 'speed',

  // Load-bearing. The window IS isi_ms, which is the presentation interval for
  // the card the NEXT trial is compared against — end a trial early and the
  // card the participant must hold gets a shorter, variable exposure, which is
  // the manipulation the ladder escalates. The prime trial accepts no response
  // at all and would in any case run its full window.
  holdsFullWindow: true,

  instructions: [
    'Cards appear one at a time.',
    'Say whether each card is the SAME as the one just before it.',
    'Shape, colour and how many all have to match.',
    'The very first card of each round is just a starting point — nothing to answer.',
  ],
  // Negative LEFT, affirmative RIGHT — the battery-wide convention, so a
  // participant moving between instruments never has to re-learn which side
  // means "yes". ResponseTargets renders this array left-to-right with no
  // reordering, so the keys and hint belong to the POSITION, not to the id:
  // ArrowLeft/f must always sit on the left button whatever it answers.
  targets: [
    { id: 'different', label: 'Different', keys: ['ArrowLeft', 'f'], hint: '←' },
    { id: 'same', label: 'Same', keys: ['ArrowRight', 'j'], hint: '→' },
  ],

  // NOT in target order, unlike the rest of the battery. These three rows are
  // one running sequence — each card is judged against the row above it — so
  // reordering them would make the notes describe the wrong comparison.
  examples: [
    { stimulus: { shape: 'triangle', color: 'blue', count: 2 }, response: 'Starting card' },
    {
      stimulus: { shape: 'triangle', color: 'blue', count: 2 },
      response: 'Same',
      note: 'identical to the card before',
    },
    {
      stimulus: { shape: 'triangle', color: 'blue', count: 3 },
      response: 'Different',
      note: 'count changed',
    },
  ],

  toAnswer: (targetId) => ({ isMatch: targetId === 'same' }),

  // Trial 0 of EVERY block — not just block 0 — establishes the card the next
  // trial is compared against. It has no correct answer and is submitted with
  // all three response fields null, which the server grades as undefined rather
  // than wrong. Identified by index because trialType is not on the wire: it was
  // removed from the client projection when it was found to be a verbatim
  // relabelling of the answer on this task (match/different IS isMatch). The
  // index is exact — the prime is always first and nothing else is unjudged.
  acceptsResponse: (trial) => trial?.trialIndex !== 0,

  // isi_ms is the presentation interval and doubles as the response window.
  // differing_features is the difficulty knob and must never reach rendering —
  // it would tell the participant how many features to check.
  timing: (params) => ({
    fixationMs: 250,
    // Number(), not a bare read: this value becomes the runner's RT ceiling
    // (windowMs + 250). A string isi_ms would concatenate there rather than add,
    // yielding a ceiling like "2000250" and accepting every late response.
    // ||, not ??: a served 0 is not a real window, it is an instant timeout.
    windowMs: Number(params?.isi_ms) || DEFAULT_WINDOW_MS,
    gapMs: 0,
  }),

  // Nothing hidden: both cards were on screen, one after the other.
  practiceExpectation: (stimulus, ctx) => {
    const prev = ctx?.prevStimulus;
    if (!prev) return null;
    return sameCard(stimulus, prev) ? 'same' : 'different';
  },

  StimulusView,
};
